import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Link,
    Box,
    Alert,
    CircularProgress,
    Avatar,
} from '@mui/material';
import { CloudUpload, Business, Person } from '@mui/icons-material';
import { clearError, selectAuthLoading, selectAuthError } from '../../store/authSlice';
import { authAPI } from '../../services/api';

const Signup = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loading = useSelector(selectAuthLoading);
    const reduxError = useSelector(selectAuthError);
    const [formData, setFormData] = useState({
        companyName: '',
        companyEmail: '',
        email: '',
        password: '',
        confirmPassword: '',
        fullName: ''
    });
    const [companyLogo, setCompanyLogo] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError('Company logo must be less than 5MB');
                return;
            }
            setCompanyLogo(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError('Profile image must be less than 5MB');
                return;
            }
            setProfileImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate images
        if (!companyLogo) {
            setError('Company logo is required');
            return;
        }
        if (!profileImage) {
            setError('Profile image is required');
            return;
        }

        // Validate password match
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Validate password strength
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        setUploading(true);
        dispatch(clearError());

        try {
            // Create FormData for file upload
            const formDataToSend = new FormData();
            formDataToSend.append('company_name', formData.companyName);
            formDataToSend.append('company_email', formData.companyEmail);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('password', formData.password);
            formDataToSend.append('full_name', formData.fullName);
            formDataToSend.append('company_logo', companyLogo);
            formDataToSend.append('profile_image', profileImage);

            const response = await authAPI.signup(formDataToSend);
            const { accessToken, refreshToken, user } = response.data.data;

            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(user));

            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.message || 'Signup failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        width: '100%',
                        maxWidth: 500
                    }}
                >
                    <Typography variant="h4" component="h1" gutterBottom align="center">
                        Create Account
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                        Sign up to get started with your company
                    </Typography>

                    {(error || reduxError) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error || reduxError}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            margin="normal"
                            required
                            autoFocus
                        />

                        {/* Company Logo Upload */}
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Company Logo *
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
                                    src={logoPreview}
                                >
                                    <Business sx={{ fontSize: 40 }} />
                                </Avatar>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUpload />}
                                >
                                    Upload Logo
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                    />
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Max size: 5MB. Formats: JPG, PNG, GIF, WebP
                            </Typography>
                        </Box>

                        <TextField
                            fullWidth
                            label="Company Email"
                            name="companyEmail"
                            type="email"
                            value={formData.companyEmail}
                            onChange={handleChange}
                            margin="normal"
                            required
                            helperText="Official company email address"
                        />

                        <TextField
                            fullWidth
                            label="Full Name"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            margin="normal"
                            required
                        />

                        {/* User Profile Image Upload */}
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Your Profile Image *
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{ width: 80, height: 80, bgcolor: 'secondary.main' }}
                                    src={imagePreview}
                                >
                                    <Person sx={{ fontSize: 40 }} />
                                </Avatar>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUpload />}
                                >
                                    Upload Image
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Max size: 5MB. Formats: JPG, PNG, GIF, WebP
                            </Typography>
                        </Box>

                        <TextField
                            fullWidth
                            label="Your Email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            margin="normal"
                            required
                            autoComplete="email"
                            helperText="Your personal email for login"
                        />

                        <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            margin="normal"
                            required
                            autoComplete="new-password"
                            helperText="At least 6 characters"
                        />

                        <TextField
                            fullWidth
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            margin="normal"
                            required
                            autoComplete="new-password"
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading || uploading}
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {(loading || uploading) ? <CircularProgress size={24} /> : 'Create Account'}
                        </Button>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2">
                                Already have an account?{' '}
                                <Link component={RouterLink} to="/login" underline="hover">
                                    Sign In
                                </Link>
                            </Typography>
                        </Box>
                    </form>
                </Paper>
            </Box>
        </Container>
    );
};

export default Signup;
